<script setup lang="ts">
/**
 * PageSettingsDrawer — højre push-drawer med den AKTIVE sides indstillinger
 * (12-08-2026). Åbnes af tandhjulet (PageSettingsButton) i headeren; samme
 * push-mekanik som FilterDrawer (w-0 ↔ w-[300px], sticky, ikke-modal) og
 * gensidigt udelukkende med den (useDrawer/useSettingsDrawer).
 *
 * Indholdet er GENERISK: sidens felter slås op i shared-registry'et ud fra
 * route.path og renderes af PageSettingsFields; værdier + canEdit hentes fra
 * /api/page-settings (usePageSettings). Alle med side-adgang kan SE, kun
 * edit-grant kan GEMME — serveren håndhæver uafhængigt (fail-closed).
 */

import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { getPageSettingsDef, type PageSettingValue } from '@cockpit/shared';

import { useSettingsDrawer } from '../composables/useDrawer';
import { usePageSettings } from '../composables/usePageSettings';
import { ICONS } from '../icons';
import PageSettingsFields from './PageSettingsFields.vue';

const route = useRoute();
const { isOpen, close } = useSettingsDrawer();
const { load, entryFor, save, error: loadError } = usePageSettings();

const def = computed(() => getPageSettingsDef(route.path));
const entry = computed(() => (def.value ? entryFor(def.value.routePath) : undefined));

/** Lokal kladde — gemmes først ved klik på Gem. */
const draft = ref<Record<string, PageSettingValue>>({});
const dirty = ref(false);
const saving = ref(false);
const saveError = ref<string | null>(null);
const savedFlash = ref(false);

function resetDraft(): void {
  draft.value = { ...(entry.value?.values ?? {}) };
  dirty.value = false;
  saveError.value = null;
}

// Hent data når draweren åbnes; nulstil kladden når side/data skifter.
watch(
  [isOpen, def, entry],
  () => {
    if (isOpen.value && def.value) void load();
    if (!dirty.value) resetDraft();
  },
  { immediate: true },
);

function onFieldUpdate(key: string, value: PageSettingValue): void {
  draft.value = { ...draft.value, [key]: value };
  dirty.value = true;
}

async function submit(): Promise<void> {
  if (!def.value || saving.value) return;
  saving.value = true;
  saveError.value = null;
  try {
    await save(def.value.routePath, draft.value);
    dirty.value = false;
    savedFlash.value = true;
    setTimeout(() => (savedFlash.value = false), 2500);
  } catch (err) {
    saveError.value = err instanceof Error ? err.message : String(err);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <aside
    class="sticky top-0 h-screen overflow-hidden transition-[width] duration-200 ease-out shrink-0 relative z-40 bg-page print:hidden"
    :class="isOpen ? 'w-[300px]' : 'w-0'"
    :aria-hidden="!isOpen"
  >
    <div class="absolute left-0 top-0 w-px h-full bg-line/60" aria-hidden="true"></div>

    <div class="w-[300px] flex flex-col h-full">
      <header class="flex items-center justify-between h-14 px-4">
        <h2 class="text-sm font-medium text-ink">Indstillinger</h2>
        <button
          type="button"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-muted hover:bg-hover hover:text-ink transition-colors"
          aria-label="Luk indstillinger"
          @click="close"
        >
          <span class="block w-5 h-5" v-html="ICONS.close" />
        </button>
      </header>

      <div class="flex-1 overflow-y-auto scrollbar-thin px-4 pb-4">
        <template v-if="def">
          <p class="mb-4 text-[11px] text-faint">{{ def.description }}</p>

          <p v-if="loadError" class="mb-3 text-xs text-red">
            Indstillingerne kunne ikke hentes: {{ loadError }}
          </p>

          <template v-if="entry">
            <PageSettingsFields
              :def="def"
              :values="draft"
              :disabled="!entry.canEdit"
              @update="onFieldUpdate"
            />

            <p v-if="!entry.canEdit" class="mt-4 text-[11px] text-faint">
              Du har læseadgang. Ændringer kræver redigeringsret til siden
              (Indstillinger → Brugere).
            </p>

            <div v-else class="mt-5 flex items-center gap-2">
              <button
                type="button"
                :disabled="!dirty || saving"
                class="px-3 py-1.5 rounded-lg bg-ink text-surface text-xs font-medium hover:opacity-80 transition-opacity disabled:opacity-40"
                @click="submit"
              >
                {{ saving ? 'Gemmer …' : 'Gem' }}
              </button>
              <button
                type="button"
                :disabled="!dirty || saving"
                class="px-3 py-1.5 rounded-lg border border-line text-xs text-ink hover:bg-hover transition-colors disabled:opacity-40"
                @click="resetDraft"
              >
                Fortryd
              </button>
              <span v-if="savedFlash" class="text-[11px] text-muted">Gemt.</span>
            </div>
            <p v-if="saveError" class="mt-2 text-xs text-red">Kunne ikke gemme: {{ saveError }}</p>
          </template>
          <p v-else class="text-sm text-muted">Henter indstillinger …</p>
        </template>

        <p v-else class="text-sm text-muted">Denne side har ingen indstillinger.</p>
      </div>
    </div>
  </aside>
</template>
